import React from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from './Header';
import AppSidebar from './AppSidebar';
import ClientSelector from './ClientSelector';
import CoryChat from './CoryChat';
import { LocationAlertBanner } from './alerts/LocationAlertBanner';
import CopyLinkButton from './CopyLinkButton';
import { AccountingGateProvider, useAccountingGate } from '../contexts/AccountingGateContext';
import { useAuth } from '../contexts/AuthContext';
import SetupGuide from './onboarding/SetupGuide';
import { MfaEnrollmentGate } from './auth/MfaEnrollmentGate';

/**
 * Hard gate: a real (non-demo) client must connect an accounting source before
 * any dashboard renders. The setup screen itself (/app/setup/connect) is always
 * allowed through so it can drive the connection flow.
 */
function AccountingGate({ children }) {
  const { loading, allowed } = useAccountingGate();
  const location = useLocation();
  const onSetup = location.pathname.startsWith('/app/setup/connect');

  if (onSetup) return children;
  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center" data-testid="accounting-gate-loading">
        <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!allowed) return <Navigate to="/app/setup/connect" replace />;
  return children;
}

/**
 * Setup Guide gate: a live (non-demo) client who hasn't completed onboarding
 * must finish the Setup Guide (logo, accounting, team, brand overview) before
 * the guided tour runs. Rendered at layout level so it gates every /app page,
 * regardless of which dashboard they land on.
 */
function SetupGuideGate() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // The guide is only fully dismissed once the user STARTS the tour (onReady).
  // Closing it / navigating to a step now MINIMISES it to a persistent
  // launcher so the customer can always find their way back — instead of the
  // old behaviour where the first step click hid the guide for the session.
  const [done, setDone] = React.useState(
    () => sessionStorage.getItem('corvana_setup_guide_done') === '1'
  );
  const [minimized, setMinimized] = React.useState(
    () => sessionStorage.getItem('corvana_setup_guide_min') === '1'
  );
  const [status, setStatus] = React.useState(null);

  const live = !!user && user.is_demo !== true && user.onboarding_completed === false;

  const minimise = React.useCallback(() => {
    sessionStorage.setItem('corvana_setup_guide_min', '1');
    setMinimized(true);
  }, []);

  const reopen = React.useCallback(() => {
    sessionStorage.removeItem('corvana_setup_guide_min');
    setMinimized(false);
  }, []);

  if (!live || done || location.pathname.startsWith('/app/setup/connect')) return null;

  const total = status?.total ?? 4;
  const completed = status?.completed ?? 0;

  return (
    <>
      <SetupGuide
        open={!minimized}
        onStatusChange={setStatus}
        onReady={() => {
          sessionStorage.setItem('corvana_setup_guide_done', '1');
          sessionStorage.removeItem('corvana_setup_guide_min');
          setDone(true);
          navigate('/app/dashboard');
        }}
        onClose={minimise}
      />
      {minimized && (
        <button
          type="button"
          onClick={reopen}
          data-testid="setup-guide-launcher"
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white pl-3.5 pr-4 py-2.5 shadow-xl shadow-blue-900/20 ring-1 ring-white/20 hover:scale-[1.03] active:scale-95 transition-transform"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
          </span>
          <span className="text-sm font-semibold">Finish setup</span>
          <span className="text-[11px] font-medium bg-white/20 rounded-full px-2 py-0.5" data-testid="setup-guide-launcher-progress">
            {completed}/{total}
          </span>
        </button>
      )}
    </>
  );
}

export default function MainLayout() {
  return (
    <AccountingGateProvider>
      <div className="min-h-screen bg-background transition-colors duration-300">
        {/* Cory AI Assistant (floating chat) */}
        <CoryChat />
        
        {/* Location Performance Alerts */}
        <LocationAlertBanner />
        
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50 transition-colors duration-300">
          <Header />
        </div>

        {/* Two-column shell: persistent side nav + main content */}
        <div className="flex flex-col lg:flex-row w-full">
          <AppSidebar />
          <main className="flex-1 min-w-0">
            <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 overflow-x-auto">
              <div className="flex justify-end items-center gap-3 mb-3">
                <ClientSelector />
                <CopyLinkButton />
              </div>
              <MfaEnrollmentGate>
                <AccountingGate>
                  <SetupGuideGate />
                  <Outlet />
                </AccountingGate>
              </MfaEnrollmentGate>
            </div>
          </main>
        </div>
      </div>
    </AccountingGateProvider>
  );
}